import React from 'react';
import { I18n } from '@aws-amplify/core';
import type { RelatedReport, RiskLibertyMutual } from '@/context/Resolution886Report/types';
import * as S from './styles';

const NIOSH_RISK = {
	VERY_LOW: 'VERY_LOW',
	LOW: 'LOW',
	MODERATE: 'MODERATE',
	HIGH: 'HIGH',
	VERY_HIGH: 'VERY_HIGH',
	NOT_AVALIATED: 'NOT_AVALIATED'
} as const;

type NioshRisk = (typeof NIOSH_RISK)[keyof typeof NIOSH_RISK];

const KIM_PUSH_PULL_RISK: Record<string, NioshRisk> = {
	LOW: NIOSH_RISK.VERY_LOW,
	MODERATE: NIOSH_RISK.LOW,
	HIGH: NIOSH_RISK.HIGH,
	NOT_AVALIATED: NIOSH_RISK.NOT_AVALIATED
};

const REBA_RISK: Record<string, NioshRisk> = {
	NEGLIGIBLE_RISK: NIOSH_RISK.VERY_LOW,
	LOW: NIOSH_RISK.LOW,
	MEDIUM: NIOSH_RISK.MODERATE,
	HIGH: NIOSH_RISK.HIGH,
	VERY_HIGH_RISK: NIOSH_RISK.VERY_HIGH,
	NOT_AVALIATED: NIOSH_RISK.NOT_AVALIATED
};

const STRAIN_INDEX_RISK: Record<string, NioshRisk> = {
	SAFE: NIOSH_RISK.VERY_LOW,
	HAZARDOUS: NIOSH_RISK.HIGH,
	NOT_AVALIATED: NIOSH_RISK.NOT_AVALIATED
};

const HAND_ARM_VIBRATION_EXPOSURE_RISK: Record<string, NioshRisk> = {
	TOLERABLE: NIOSH_RISK.VERY_LOW,
	MODERATE: NIOSH_RISK.LOW,
	NOT_TOLERABLE: NIOSH_RISK.HIGH,
	NOT_AVALIATED: NIOSH_RISK.NOT_AVALIATED
};

const WHOLE_BODY_VIBRATION_EXPOSURE_RISK: Record<string, NioshRisk> = {
	TOLERABLE: NIOSH_RISK.VERY_LOW,
	MODERATE: NIOSH_RISK.LOW,
	NOT_TOLERABLE: NIOSH_RISK.HIGH,
	NOT_AVALIATED: NIOSH_RISK.NOT_AVALIATED
};

const REPORTS_MAP: Record<string, Record<string, NioshRisk>> = {
	NIOSH: NIOSH_RISK,
	KIM_PUSH_PULL: KIM_PUSH_PULL_RISK,
	REBA: REBA_RISK,
	STRAIN_INDEX: STRAIN_INDEX_RISK,
	HAND_ARM_VIBRATION_EXPOSURE: HAND_ARM_VIBRATION_EXPOSURE_RISK,
	WHOLE_BODY_VIBRATION_EXPOSURE: WHOLE_BODY_VIBRATION_EXPOSURE_RISK
};

const REPORT_RISK_NAME = {
	SAFE: 'Safe',
	LOW: 'Low risk',
	HIGH: 'High risk',
	MEDIUM: 'Medium risk',
	HAZARDOUS: 'Hazardous',
	TOLERABLE: 'Tolerable risk',
	NOT_TOLERABLE: 'Not tolerable risk',
	VERY_LOW: 'Very low risk',
	MODERATE: 'Moderate risk',
	VERY_HIGH: 'Very high risk',
	NEGLIGIBLE_RISK: 'Negligible risk',
	VERY_HIGH_RISK: 'Very high risk',
	VERY_LOW_RISK: 'Very low risk',
	NOT_AVALIATED: 'Not evaluated'
};

interface ErgonomicRiskBadgeProps {
	relatedReport: RelatedReport;
}

const LIBERTY_MUTUAL_KEY = 'liberty_mutual';

export function ErgonomicRiskBadge({ relatedReport }: Readonly<ErgonomicRiskBadgeProps>): JSX.Element {
	const isLibertyMutual = relatedReport.name === LIBERTY_MUTUAL_KEY;
	const reportName = relatedReport.name.toUpperCase();

	function handleRiskLevelMap(reportName: string, riskValue: string): NioshRisk | null {
		const reportMap = REPORTS_MAP[reportName];

		if (!reportMap) {
			return null;
		}

		return reportMap[riskValue] ?? null;
	}

	const color = !isLibertyMutual ? handleRiskLevelMap(reportName, relatedReport.result.risk as string) : null;

	if (isLibertyMutual) {
		return (
			<S.ContainerBadgeLibertyMutual>
				<div>
					<S.CustomLabel>{I18n.get('Man')}</S.CustomLabel>
					<S.CustomBadgeLibertyMutual>
						{(relatedReport.result?.risk as RiskLibertyMutual)?.male_score}
					</S.CustomBadgeLibertyMutual>
				</div>
				<div>
					<S.CustomLabel>{I18n.get('Woman')}</S.CustomLabel>
					<S.CustomBadgeLibertyMutual>
						{(relatedReport.result?.risk as RiskLibertyMutual)?.female_score}
					</S.CustomBadgeLibertyMutual>
				</div>
			</S.ContainerBadgeLibertyMutual>
		);
	}

	if (!color) {
		return <></>;
	}

	const message = I18n.get(REPORT_RISK_NAME[relatedReport.result.risk as keyof typeof REPORT_RISK_NAME]);

	return <S.CustomBadge color={color}>{message}</S.CustomBadge>;
}
